<script setup lang="ts">
import { EStoreModules } from '@/store/storeType'
import type { ProductionLineModel } from '@/types/models/ProductionLineModel'
import {
    CreateButton,
    FullTableLayout,
    MobileShowFiltersButton,
    StackLayout
} from '@rfc/rfc-components-library'
import { useTableModel } from '@rfc/rfc-components-library/hooks'
import type { FullTableLayoutActionProps } from '@rfc/rfc-components-library/types'
import { getQueryFilters } from '@rfc/rfc-components-library/utils'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { PRODUCTION_LINE_ROUTES } from '../../../router/routes'
import { EProductionLineActions } from '../../../store/productionLinesStore/ProductionLineStoreTypes'
import CreateProductionLineDialog from './CreateProductionLineDialog.vue'

const { t } = useI18n()
const router = useRouter()
const store = useStore()

type Props = {
    actionProps?: FullTableLayoutActionProps<ProductionLineModel>
    baseUrl?: string
}
defineProps<Props>()

const dialogCreateOpen = ref(false)
const columns = computed(() => [
    { title: t('productionLine.columns.id'), key: 'id' },
    { title: t('productionLine.columns.name'), key: 'name' }
])

const productionLinesModel = useTableModel<ProductionLineModel, { organization_id?: number }>({
    store,
    stateKey: EStoreModules.PRODUCTION_LINE,
    stateAction: EProductionLineActions.FETCH_PRODUCTION_LINES,
    router,
    dataFilters: {
        ...getQueryFilters()
    }
})

const setActionsProps: FullTableLayoutActionProps<ProductionLineModel> = {
    titleItemKey: 'name',
    editPermission: {
        href: PRODUCTION_LINE_ROUTES.baseUrl,
        hasPermission: true
    }
}
</script>

<template>
    <StackLayout direction="column" :gap="16" class="w-full h-full pa-4">
        <FullTableLayout
            :columns="columns"
            :data="productionLinesModel.data.value"
            :loading="productionLinesModel.loading.value"
            :sortBy="productionLinesModel.sort.value"
            :actionProps="{ ...setActionsProps, ...(actionProps ?? {}) }"
            @change:pageNumber="productionLinesModel.handleChangePageNumber"
            @change:pageSize="productionLinesModel.handleChangePageSize"
            @changeSortBy="productionLinesModel.handleChangeSortBy"
            @removeTag="productionLinesModel.handleRemoveTag"
            @search="productionLinesModel.handleSearch"
            translationPath="event.filters"
            class="h-auto">
            <template #filters>
                <MobileShowFiltersButton>
                    <slot
                        name="filters"
                        :filters="productionLinesModel.filters.value"
                        @change="productionLinesModel.handleChangeFilters" />
                </MobileShowFiltersButton>
            </template>

            <template #button>
                <CreateButton
                    :label="$t('productionLine.create')"
                    @click="dialogCreateOpen = true" />
            </template>
        </FullTableLayout>

        <CreateProductionLineDialog
            :open="dialogCreateOpen"
            :routeToGo="baseUrl"
            @close="dialogCreateOpen = false"
            @refresh="productionLinesModel.getData">
            <template #create="props">
                <slot name="create" :data="props.data" @change="props.onChange" />
            </template>
        </CreateProductionLineDialog>
    </StackLayout>
</template>
